import { isFilled } from "@/ipqc/lib/utils"
import { toPathString } from "@/lib/form/form"
import {
  ParamSchema,
  ParamSchemaMode,
  ParamSchemaType,
  ParamValidationType,
} from "@/lib/form/schema"
import { FormParamStatus } from "@/lib/form/types"
import {
  ParamAPISchema,
  PMProductSchema,
  PMSku,
  RMPMParameterSchema,
  RMProductSchema,
} from "@/services/rmpm"
import { LabelValue } from "@/types/common"
import { get, omit, set, toPath } from "lodash"

/**
 * Schema that describes a form's value object and operations
 */
export interface Schema {
  id?: string
  parameterId?: string
  productId?: string
  sheetId?: string           // Server-generated sheet ID (for trucks)
  productSheetId?: string    // Server-generated product sheet ID (for products)
  name?: string
  type?: DataType
  format?: string
  required?: boolean
  default?: PrimitiveValue | Record<string, PrimitiveValue>
  isHandover?: boolean
  isResample?: boolean
  status?: FormParamStatus

  // Enum field
  enum?: PrimitiveValue

  // Exact value
  const?: PrimitiveValue

  // Array related fields
  items?: Schema[]
  minItems?: number
  maxItems?: number

  // Number related fields
  minimum?: number
  exclusiveMinimum?: number
  maximum?: number
  exclusiveMaximum?: number

  // Additional type of validations
  requireRemarks?: boolean

  // Object related fields
  properties?: Record<string, Schema>
  skus?: Record<string, Schema>

  // Conditional fields
  allOf?: Schema[]
  oneOf?: Schema[]

  if?: Schema
  /**
   * If a path is specified then the schema is run
   * against the context of that path
   */
  path?: string
  then?: Schema

  // Actions to be executed on an event
  actions?: {
    change?: SchemaAction[]
    remove?: SchemaAction[]
  }

  options?: {
    mode?: ParamSchemaMode
    unit?: string
    label?: string
    subtext?: string
    hidden?: boolean
    disabled?: boolean
    hideLabel?: boolean
    widget?: ParamSchemaType
    choices?: LabelValue[]

    addable?: boolean
    /**
     * Details for adding schema
     */
    addFrom?: {
      path: string
      if?: Schema
      then?: Schema
    }

    removable?: boolean

    /**
     * This is used to add the legacy validation
     */
    paramSchema?: ParamSchema
  }
}

interface ProductSKU {
  id: string
  name: string
  data: PMSku
}

export interface FormParameter {
  id?: string; // Unique ID (parameterId or generated)
  label: string;
  recordedAt: string;
  value: string;
  status: FormParamStatus;
  required: boolean;
  parameterId?: string; // Optional: ID used to link back to schema
  productId?: string; // Optional: ID of the product this parameter belongs to
  inputType?: string; // Optional: input type for form rendering
  remark?: string;
  parameters?: FormParameter[]; // Used for nested parameters (e.g., inside 'products')
}

export interface BaseParameter {
  parameterId: string;
  name: string;
  value?: any;
  status: FormParamStatus | string;
  remark?: string;
}

export interface MarketParameters {
  parameters: BaseParameter[];
}

export interface MarketType {
  export?: MarketParameters;
  domestic?: MarketParameters;
}

export interface TruckParameter extends BaseParameter {
  inputType: string;
}

export interface ProductMarket {
  market?: MarketType;
}

export interface SKU {
  id: string;
  market?: MarketType;
}

export interface Product {
  id: string;
  name: string;
  productId?: string;
  value?: any;
  status: FormParamStatus | string;
  parameters: BaseParameter[];
  market?: MarketType;
  sku: SKU[];   // 👈 fixed from tuple to proper array
}

export interface TruckData {
  sheetId: string;
  schema: string; // JSON string of metaSchema
  type: string;

  truckParameters?: TruckParameter[];
  products?: Product[];
}


export interface TruckForm {
  sheetId: string; // Server-generated sheet ID
  general: FormParameter;
  products: FormParameter;
}

type PrimitiveValue = string | number | boolean

export interface SchemaAction {
  path: string
  op: SchemaOperation
  condition: Schema
  schema: Schema
}

export type SchemaOperation = "add" | "replace" | "remove"

export type DataType =
  | "boolean"
  | "string"
  | "number"
  | "array"
  | "null"
  | "object"

export class SchemaBuilder {
  metaSchema: Schema
  schema: Schema

  constructor(metaSchema: Schema) {
    this.metaSchema = metaSchema
    this.schema = {
      type: "null",
    }
  }

  static omittedInSchema = ["if", "then", "allOf", "oneOf"]

  updateSchema(path: string, value: Schema | undefined) {
    if (!path) {
      this.schema = value || {}
    } else {
      const parts = toPath(path)
      if (parts.at(-2) === "items") {
        const listPath = parts.slice(0, -1)
        let list: Schema["items"] = get(this.schema, listPath)

        if (!list) {
          list = []
          set(this.schema, listPath, list)
        }

        list.push(value || {})

        return
      }

      const cleaned = omit(value, SchemaBuilder.omittedInSchema)
      set(this.schema, path, cleaned)
    }
  }

  refineMetaSchema(
    values?: Record<string, any>,
    prefix = ""
  ): Schema | undefined {

    if (!prefix) {
      this.schema = {}
    }

    let curr: Schema | undefined = this.metaSchema
    if (prefix) {
      curr = get(this.metaSchema, prefix) as Schema | undefined
    }

    if (!curr) {
      return this.schema
    }

    const valuePrefix = schemaPathToValuePath(prefix)

    let passed = false
    if (curr.if) {
      if (curr.if.allOf) {
        passed = true
        for (const item of curr.if.allOf) {
          let valuePath = valuePrefix

          if (item.path) {
            valuePath = schemaPathToValuePath(item.path)
          }

          if (valuePrefix.includes("sku")) {
            const parts = valuePrefix.split(".")
            parts.pop()
            const parts2 = getLastTwo(valuePath)
            valuePath = [...parts, ...parts2].join(".");
          }

          const context = get(values, valuePath)
          if (context !== item.const) {
            passed = false

            break
          }
        }

        if (passed) {
          this.updateSchema(prefix, curr.then)
        }
      } else if (curr.if.oneOf) {
        passed = false
        for (const item of curr.if.oneOf) {
          let valuePath = valuePrefix
          if (item.path) {
            valuePath = schemaPathToValuePath(item.path)
          }

          const context = get(values, valuePath)

          if (context === item.const) {
            passed = true

            break
          }
        }

        if (passed) {
          this.updateSchema(prefix, curr.then)
        }
      } else {
        let context = get(values, valuePrefix)
        if (curr.path) {
          context = get(values, schemaPathToValuePath(curr.path))
        }

        if (context === curr.const) {
          passed = true
          this.updateSchema(prefix, curr.then)
        }
      }
    } else {
      passed = true
      this.updateSchema(prefix, omit(curr, ["properties", "items"]))
    }

    if (curr.properties) {
      for (const field in curr.properties) {

        const newPath = toPathString([prefix, "properties", field])

        this.refineMetaSchema(values, newPath)
      }

      return this.schema
    }

    if (curr.items) {
      for (const [index] of curr.items.entries()) {

        this.refineMetaSchema(
          values,
          toPathString([prefix, "items", `${index}`])
        )
      }

      return this.schema
    }

    return this.schema
  }
}

function getLastTwo(path: string) {
  const parts = path.split(".");
  return parts.slice(-2); // last 2 items
}

export function schemaPathToValuePath(path: string): string {
  const parts = toPath(path).filter((part) => {
    if (part === "properties" || part === "items") {
      return false
    }

    return true
  })

  return toPathString(parts)
}

export function formatLimit(limit?: { min?: number; max?: number }): string {
  if (
    !limit ||
    typeof limit.min !== "number" ||
    typeof limit.max !== "number"
  ) {
    return ""
  }

  if (limit.max === limit.min) {
    return `${limit.min}`
  }

  return `${limit.min} - ${limit.max}`
}

export function buildSchemaForGeneralParameter(data: RMPMParameterSchema[], truck?: any): Schema[] {
  return data
    .map((field) => {
      const isChoice = field.inputType === "CHOICE";

      const baseItem: Schema = {
        id: field.id,
        parameterId: field.id,  // Use actual server ID for hydration
        type: "object",
        required: field.mandatory,
        options: {
          label: field.name,
          widget: isChoice ? "CHOICE" : ParamSchemaType.VALUE_TEXT,
          mode: field.mode,
          disabled: field.isEditable === false,
        },
        properties: {
          id: {
            type: "string",
            default: field.id,
            required: true,
          },
          recordedAt: {
            type: "string",
            required: true,
          },
          value: {
            type: "string",
            required: field.mandatory,
          },
        },
      };

      if (isChoice && field.possibleValues) {
        baseItem.options!.choices = field.possibleValues.map((val) => ({
          id: val,
          label: val,
          value: val,
          meta: { isCorrect: val === field.correctValue },
        }));
      }

      if (!field.isEditable && truck?.pmType) {
        baseItem.properties!.value!.default = truck.pmType;
      }

      return baseItem;
    });

}

export function buildSchemaForPmProduct(
  product: PMProductSchema,
  root: string
): Schema {

  /* ==============================
     1. BASE SCHEMA
  ============================== */

  const schema: Schema = {
    id: product.id,
    type: "object",
    options: {
      label: product.name,
      widget: ParamSchemaType.CARD,
      removable: true,
    },
    properties: {
      id: {
        type: "string",
        default: product.id,
        const: product.id,
        required: true,
      },
      parameters: {
        type: "array",
        items: [],
      }
    }
  }

  const parameters = schema.properties!.parameters;

  if (!parameters.items) parameters.items = [];

  const marketValuePath = `${root}.properties.parameters.items.[0].value`;


  /* ==============================
     2. MARKET CHOICES
  ============================== */

  const marketChoices: LabelValue[] = [
    { id: "domestic", label: "Domestic", value: "Domestic" },
    { id: "export", label: "Export", value: "Export" }
  ];


  /* ==============================
     3. PREPARE SKU LIST
  ============================== */

  const skus: ProductSKU[] = product.sku
    ? product.sku.map(sku => ({
      id: sku.id,
      name: sku.name,
      data: sku
    }))
    : [];

  /* ==============================
     4. PARAMETER SCHEMA GENERATOR
  ============================== */

  const buildParamSchema = (
    param: any,
    conditions: any[],
    productId: string,
    extraChoices?: LabelValue[],
  ): Schema => {

    let valueType: DataType =
      param.inputType === ParamSchemaType.VALUE_NUMBER ? "number" : "string";

    const valueSchema: Schema = {
      type: valueType,
      required: param.mandatory,
    };

    if (param.validationType === ParamValidationType.LIMIT) {
      valueSchema.minimum = param.minLimit;
      valueSchema.maximum = param.maxLimit;
    }

    let choices: LabelValue[] = extraChoices || [];

    if (
      param.inputType === ParamSchemaType.CHOICE ||
      param.inputType === ParamSchemaType.RADIO ||
      param.inputType === ParamSchemaType.DROPDOWN
    ) {
      if (param.possibleValues) {
        choices = param.possibleValues.map((choice: string) => ({
          id: choice,
          label: choice,
          value: choice,
          meta: { isCorrect: param.correctValue === choice }
        }));
      }
    }

    const subtext =
      param.validationType === ParamValidationType.LIMIT
        ? formatLimit({ min: param.minLimit, max: param.maxLimit })
        : "";

    return {
      if: { allOf: conditions },
      then: {
        type: "object",
        id: param.id,
        parameterId: param.id,  // Use actual server ID for hydration
        productId: productId,
        options: {
          mode: param.mode,
          widget: param.inputType,
          label: param.name,
          unit: param.unit,
          subtext,
          choices: choices.length ? choices : undefined,
          paramSchema: toParamSchema(param),
        },
        properties: {
          id: { type: "string", const: param.id, default: param.id, required: true },
          recordedAt: { type: "string", required: true },
          value: valueSchema,
          remark: { type: "string" },
          resample: {
            type: "object",
            properties: {
              value: { type: "string" },
              recordedAt: { type: "string" },
              remark: { type: "string" },
            }
          }
        }
      }
    };
  };

  // Use actual product ID from schema, not random UUID
  const productId: string = product.id;

  /* ==============================
     5. WITHOUT SKU FLOW
  ============================== */

  if (!skus.length) {

    // Use deterministic ID for Market field based on product ID
    const paramID: string = `${product.id}-market`;

    if (marketChoices.length > 0) {
      parameters.items!.push({
        id: paramID,
        parameterId: paramID,  // Use deterministic ID for hydration
        type: "object",
        options: {
          label: "Market",
          widget: ParamSchemaType.RADIO,
          mode: ParamSchemaMode.INFO,
          choices: marketChoices
        },
        properties: {
          id: { type: "string", default: paramID, required: true },
          recordedAt: { type: "string", required: true },
          value: { type: "string", default: marketChoices.at(0)?.value, required: true },
        },
      });
    }

    const processMarket = (market: any, marketType: string) => {

      if (!market) return;


      for (const param of market.parameters) {

        // Market Field
        if (param.name === "Market") {
          continue;
        }

        parameters.items!.push(
          buildParamSchema(
            param,
            [{ path: marketValuePath, const: marketType }],
            productId
          )
        );
      }
    };

    processMarket(product.market?.domestic, "Domestic");
    processMarket(product.market?.export, "Export");
  }

  /* ==============================
     6. WITH SKU FLOW
  ============================== */

  else {

    schema.properties!.skus = {
      type: "array",
      options: {
        addable: true,
        addFrom: { path: root },
        label: "SKU",
        hideLabel: true,
        widget: ParamSchemaType.PLAIN,
      },
      items: [],
    };

    const skuChoices: LabelValue[] = skus.map(sku => ({
      id: sku.id,
      label: sku.name,
      value: sku.id
    }));

    const skuValuePath = `${root}.properties.parameters.items[1].value`;

    // Use deterministic IDs for Market and SKU fields based on product ID
    const marketParamID: string = `${product.id}-market`;
    const skuParamID: string = `${product.id}-sku`;

    if (marketChoices.length > 0) {
      parameters.items!.push({
        id: marketParamID,
        parameterId: marketParamID,  // Use deterministic ID for hydration
        type: "object",
        options: {
          label: "Market",
          widget: ParamSchemaType.RADIO,
          mode: ParamSchemaMode.INFO,
          choices: marketChoices
        },
        properties: {
          id: { type: "string", default: marketParamID, required: true },
          recordedAt: { type: "string", required: true },
          value: { type: "string", default: marketChoices.at(0)?.value, required: true },
        },
      });
    }

    if (skuChoices.length > 0) {
      parameters.items!.push({
        id: skuParamID,
        parameterId: skuParamID,  // Use deterministic ID for hydration
        type: "object",
        options: {
          label: "SKU",
          widget: ParamSchemaType.DROPDOWN,
          mode: ParamSchemaMode.INFO,
          choices: skuChoices
        },
        properties: {
          id: { type: "string", default: skuParamID, required: true },
          recordedAt: { type: "string", required: true },
          value: { type: "string", default: skuChoices.at(0)?.id, required: true },
        },
      });
    }

    for (const sku of skus) {

      for (const marketType of ["Domestic", "Export"]) {

        const marketData =
          marketType === "Domestic"
            ? sku.data.market.domestic
            : sku.data.market.export;

        if (!marketData) continue;

        for (const param of marketData.parameters) {

          // Market field
          if (param.name === "Market") {
            continue;
          }
          // SKU field
          if (param.name === "SKU") {
            continue;
          }

          parameters.items!.push(
            buildParamSchema(
              param,
              [
                { path: skuValuePath, const: sku.id },
                { path: marketValuePath, const: marketType }
              ],
              productId
            )
          );
        }
      }
    }
  }

  return schema;
}

function toParamSchema(apiSchema: ParamAPISchema): ParamSchema {
  return {
    id: apiSchema.id,
    parameterId: apiSchema.parameterId,
    productId: apiSchema.productId,
    name: apiSchema.name,
    mode: apiSchema.mode,
    type: apiSchema.inputType,
    validationType: apiSchema.validationType,
    unit: apiSchema.unit || "",
    choices: apiSchema.possibleValues?.map((item) => ({
      id: item,
      label: item,
      value: item,
      isCorrect: item === apiSchema.correctValue,
    })),
    limit:
      isFilled(apiSchema.minLimit) && isFilled(apiSchema.maxLimit)
        ? {
          min: apiSchema.minLimit as number,
          max: apiSchema.maxLimit as number,
          gradeRanges: [],
        }
        : undefined,
  }
}

export function buildSchemaForRmProduct(product: RMProductSchema): Schema {

  // Use actual product ID from schema, not random UUID
  const productId: string = product.id;

  const schema: Schema = {
    id: product.id,
    type: "object",
    options: {
      label: product.name,
      widget: ParamSchemaType.CARD,
      removable: true,
    },
    properties: {
      id: {
        type: "string",
        required: true,
        default: product.id,
        const: product.id,
      },
      parameters: {
        type: "array",
        items: [],
      },
    },
  }

  if (!schema?.properties) {
    schema.properties = {}
  }

  const parameters = schema.properties.parameters
  if (!parameters?.items) {
    parameters.items = []
  }

  for (const param of product.parameters) {
    let valueType: DataType = "string"
    if (param.inputType === ParamSchemaType.VALUE_NUMBER) {
      valueType = "number"
    }

    const valueSchema: Schema = {
      type: valueType,
      required: true,
    }

    let subtext = ""
    if (param.validationType === ParamValidationType.LIMIT) {
      valueSchema.minimum = param.minLimit
      valueSchema.maximum = param.maxLimit

      subtext = formatLimit({ min: param.minLimit, max: param.maxLimit })
    }

    let choices: LabelValue[] = []
    if (
      param.inputType === ParamSchemaType.CHOICE ||
      param.inputType === ParamSchemaType.RADIO ||
      param.inputType === ParamSchemaType.DROPDOWN
    ) {
      if (param.possibleValues) {
        choices = param.possibleValues?.map((choice) => ({
          id: choice,
          label: choice,
          value: choice,
          meta: {
            isCorrect: param.correctValue === choice,
          },
        }))
      }
    }

    parameters.items!.push({
      type: "object",
      id: param.id,
      parameterId: param.id,  // Use actual server ID for hydration
      productId: productId,
      options: {
        mode: param.mode,
        unit: param.unit,
        label: param.name,
        subtext: subtext,
        widget: param.inputType,
        choices: choices.length ? choices : undefined,
        paramSchema: toParamSchema(param),
      },
      properties: {
        id: {
          type: "string",
          const: param.id,
          default: param.id,
          required: true,
        },
        recordedAt: {
          type: "string",
          required: true,
        },
        value: valueSchema,
        remark: {
          type: "string",
        },
        resample: {
          type: "object",
          properties: {
            value: {
              type: "string",
            },
            recordedAt: {
              type: "string",
            },
            remark: {
              type: "string",
            },
          },
        },
      },
    })
  }

  return schema
}

const getParamStatus = (status: FormParamStatus | string): FormParamStatus =>
  status === FormParamStatus.PENDING ? FormParamStatus.PENDING : FormParamStatus.SUBMITTED;


function buildProductParameters(
  product: any,
  productId: string,
  isSkuItem: boolean = false,
  schemaItems: Schema[] = []
): FormParameter[] {

  const recordedAt = new Date().toISOString();

  const defaultValue: FormParameter = {
    id: "",
    label: "",
    parameterId: "",
    recordedAt,
    remark: "",
    required: true,
    status: FormParamStatus.SUBMITTED,
    value: "",
  };

  // Helper to find API parameter by matching parameterId to schema's parameterId
  const findApiParam = (schemaItem: Schema, apiParams: any[]) => {
    return apiParams.find((p: any) => p.parameterId === schemaItem.parameterId);
  };

  // Helper to build form parameter from schema item and API data
  const buildFormParam = (schemaItem: Schema, apiParam: any): FormParameter => {
    if (apiParam) {
      return {
        id: schemaItem.id || apiParam.parameterId,
        parameterId: schemaItem.parameterId || apiParam.parameterId,
        productId: productId,
        recordedAt: apiParam.recordedAt || recordedAt,
        value: String(apiParam.value ?? ""),
        status: getParamStatus(apiParam.status),
        required: apiParam.status === FormParamStatus.PENDING,
        label: apiParam.name || schemaItem.options?.label || "",
        remark: apiParam.remark || "",
      };
    }
    // No API data found, return empty form param with schema's id
    return {
      ...defaultValue,
      id: schemaItem.id || "",
      parameterId: schemaItem.parameterId || "",
      productId: productId,
      label: schemaItem.options?.label || "",
    };
  };

  // ====================================
  // 1. If product already has parameters (RM case)
  // ====================================
  if (product?.parameters?.length) {
    // If we have schema items, match by parameterId
    if (schemaItems.length > 0) {
      return schemaItems.map((schemaItem: Schema) => {
        const apiParam = findApiParam(schemaItem, product.parameters);
        return buildFormParam(schemaItem, apiParam);
      });
    }
    // Fallback: no schema items, use API data directly
    return product.parameters.map((param: any) => ({
      id: param.parameterId,
      parameterId: param.parameterId,
      productId: productId,
      recordedAt,
      value: String(param.value ?? ""),
      status: getParamStatus(param.status),
      required: param.status === FormParamStatus.PENDING,
      label: param.name,
      remark: param.remark || "",
    }));
  }

  // ====================================
  // 2. Prepare Market Parameters Source (PM case)
  // ====================================

  let marketType: "Domestic" | "Export" | null = null;
  let marketParameters: any[] = [];

  if (product?.market?.domestic?.parameters?.length) {
    marketType = "Domestic";
    marketParameters = product.market.domestic.parameters;
  }
  else if (product?.market?.export?.parameters?.length) {
    marketType = "Export";
    marketParameters = product.market.export.parameters;
  }

  if (!marketType) return [];

  // ====================================
  // 3. Core Parameter Mapping
  // ====================================

  let baseParams: FormParameter[];

  // If we have schema items, match by parameterId
  if (schemaItems.length > 0) {
    baseParams = schemaItems
      .filter((schemaItem: Schema) => {
        // Skip Market and SKU system fields - they're handled separately
        const label = schemaItem.options?.label;
        return label !== "Market" && label !== "SKU";
      })
      .map((schemaItem: Schema) => {
        const apiParam = findApiParam(schemaItem, marketParameters);
        return buildFormParam(schemaItem, apiParam);
      });
  } else {
    // Fallback: no schema items, use API data directly
    baseParams = marketParameters.map((param: any) => ({
      id: param.parameterId,
      parameterId: param.parameterId,
      recordedAt,
      value: String(param.value ?? ""),
      status: getParamStatus(param.status),
      required: param.status === FormParamStatus.PENDING,
      label: param.name,
      remark: param.remark || "",
    }));
  }

  // ====================================
  // 4. Prepend Market + SKU Fields
  // ====================================

  // Find Market schema item to get its id/parameterId
  const marketSchemaItem = schemaItems.find((s: Schema) => s.options?.label === "Market");
  const systemParams: FormParameter[] = [
    {
      ...defaultValue,
      id: marketSchemaItem?.id || `${productId}-market`,
      parameterId: marketSchemaItem?.parameterId || `${productId}-market`,
      label: "Market",
      value: marketType,
    }
  ];

  if (isSkuItem) {
    // Find SKU schema item to get its id/parameterId
    const skuSchemaItem = schemaItems.find((s: Schema) => s.options?.label === "SKU");
    systemParams.push({
      ...defaultValue,
      id: skuSchemaItem?.id || `${productId}-sku`,
      parameterId: skuSchemaItem?.parameterId || `${productId}-sku`,
      label: "SKU",
      value: product.id,
    });
  }

  return [...systemParams, ...baseParams];
}

export function buildTruckForm(truck: TruckData, truckSchema: Schema = {}, truckType: string = "rm"): TruckForm {

  const recordedAt = new Date().toISOString();

  /* ----------------------------------
     1. GENERAL PARAMETERS
  ---------------------------------- */

  const generalParams = truck?.truckParameters || [];

  let general: any = {};

  const items = truckSchema?.properties?.general?.items || [];

  for (const [index, item] of items.entries()) {

    const paramItem = generalParams.find(
      el => el.parameterId === (item as any).parameterId
    );

    general[index] = {
      inputType: paramItem?.inputType || (item as any).options?.widget,
      parameterId: paramItem?.parameterId || (item as any).parameterId,
      id: (item as any).id,
      recordedAt: (paramItem as any).recordedAt,
      value: String((paramItem as any).value ?? ""),
      status: getParamStatus((paramItem as any).status),
      required: (paramItem as any).status === FormParamStatus.PENDING,
      label: (paramItem as any).name,
      remark: (paramItem as any).remark ?? "",
    };
  }

  //Metadata defaults
  general.recordedAt = general.recordedAt || recordedAt;
  general.value = general.value || "";
  general.status = general.status || FormParamStatus.DRAFT;
  general.required = general.required ?? false;
  general.label = general.label || "General Parameters";

  /* ----------------------------------
     2. PRODUCTS MAPPING
  ---------------------------------- */

  const truckProductsSchema = truckSchema?.properties?.products?.items || [];

  const truckProductsMap = truck?.products || []

  const truckProducts: any[] = [];

  if (truckType === "rm") {

    for (const [, item] of truckProductsSchema.entries()) {

      const product: any = truckProductsMap.find(p => p.id === (item as any).id);

      // Get schema items for this product's parameters
      const paramSchemaItems = (item as any).properties?.parameters?.items || [];

      truckProducts.push({
        id: product.id,
        recordedAt,
        value: String(product.value ?? ""),
        status: getParamStatus(product.status),
        required: false,
        label: product.name,
        parameters: buildProductParameters(product, product.productId ?? "", false, paramSchemaItems),
      });

    }

  } else {

    for (const [, item] of truckProductsSchema.entries()) {

      // Get schema items for this product's parameters
      const paramSchemaItems = (item as any).properties?.parameters?.items || [];

      for (const param of paramSchemaItems) {
        if (param.options?.label === "SKU") {
          continue;
        }
      }

      const product: any = truckProductsMap.find(p => p.id === (item as any).id);

      // Case 1: Product has SKU
      if (product?.sku?.length) {

        for (const skuItem of product.sku) {
          truckProducts.push({
            id: product.id,
            recordedAt,
            value: String(product.value ?? ""),
            status: getParamStatus(product.status),
            required: false,
            label: product.name,
            parameters: buildProductParameters(skuItem, product.productId ?? "", true, paramSchemaItems),
          });
        }

      }
      // Case 2: No SKU
      else {

        truckProducts.push({
          id: product.id,
          recordedAt,
          value: String(product.value ?? ""),
          status: getParamStatus(product.status),
          required: false,
          label: product.name,
          parameters: buildProductParameters(product, product.productId ?? "", false, paramSchemaItems),
        });

      }
    }
  }

  /* ----------------------------------
     3. CONVERT PRODUCTS TO OBJECT STRUCTURE
  ---------------------------------- */

  const products: any = truckProducts.reduce((acc: any, product: any, index: number) => {
    acc[index] = product;
    return acc;
  }, {});


  // Metadata for section
  products.recordedAt = recordedAt;
  products.value = "";
  products.status = FormParamStatus.DRAFT;
  products.required = false;
  products.label = "Products";


  /* ----------------------------------
     4. FINAL STRUCTURE
  ---------------------------------- */

  return {
    sheetId: truck.sheetId,
    general,
    products,
  };
}
